import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Heading, Text } from 'tio-ui/components/typography';
import { Chip } from 'tio-ui/components/buttons';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import { PhUser, PhStar, PhCalendarCheck, PhChatCircle, PhCertificate } from 'ember-phosphor-icons';

/**
 * Coach profile information
 */
export interface Coach {
  id: string;
  name: string;
  title: string;
  photo?: string;
  specialties: string[];
  rating: number;
  reviewCount: number;
  yearsExperience: number;
  credentials: string[];
  availableSlots: number;
  bio: string;
}

/**
 * CoachCard - Displays a coach profile card
 *
 * Shows coach information including photo, name, specialties,
 * credentials, ratings, and availability with booking CTA.
 *
 * @example
 * ```gts
 * <CoachCard
 *   @coach={{this.coach}}
 *   @onSchedule={{this.openScheduler}}
 * />
 * ```
 */
interface CoachCardSignature {
  Args: {
    /** Coach profile data */
    coach: Coach;
    /** Handler for schedule button click */
    onSchedule?: (coach: Coach) => void;
    /** Whether to show compact view */
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

// Helper to render star ratings
const renderStars = (rating: number) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const stars = [];

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push('full');
    } else if (i === fullStars && hasHalfStar) {
      stars.push('half');
    } else {
      stars.push('empty');
    }
  }
  return stars;
};

const CoachCard: TOC<CoachCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div
    class="bg-white rounded-2xl shadow-sm ring-1 ring-gray-950/[0.04] overflow-hidden hover:shadow-lg transition-all duration-300"
    ...attributes
  >
    {{! Header with gradient background }}
    <div
      class="bg-gradient-to-r from-violet-600 to-purple-600 p-6 text-white relative overflow-hidden"
    >
      <div class="absolute inset-0 bg-black/5"></div>
      <div class="relative flex items-center gap-4">
        {{! Photo or placeholder }}
        <div
          class="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-4 ring-white/30"
        >
          {{#if @coach.photo}}
            <img src={{@coach.photo}} alt="{{@coach.name}}" class="w-full h-full object-cover" />
          {{else}}
            <PhUser @size={{40}} class="text-white/80" />
          {{/if}}
        </div>

        <div class="flex-1">
          <Heading @level={{3}} class="text-white mb-1">
            {{@coach.name}}
          </Heading>
          <Text class="text-violet-100 text-sm">
            {{@coach.title}}
          </Text>

          {{! Rating }}
          <div class="flex items-center gap-2 mt-2">
            <div class="flex items-center gap-0.5">
              {{#each (renderStars @coach.rating) as |star|}}
                {{#if (eq star "full")}}
                  <PhStar @size={{14}} @weight="fill" class="text-amber-300" />
                {{else if (eq star "half")}}
                  <PhStar @size={{14}} @weight="fill" class="text-amber-300 opacity-50" />
                {{else}}
                  <PhStar @size={{14}} class="text-white/40" />
                {{/if}}
              {{/each}}
            </div>
            <Text class="text-violet-100 text-xs">
              {{@coach.rating}}
              ({{@coach.reviewCount}}
              reviews)
            </Text>
          </div>
        </div>
      </div>
    </div>

    {{! Body content }}
    <div class="p-6 space-y-4">
      {{! Experience & Credentials }}
      <div class="flex items-center gap-4 text-sm text-gray-600">
        <div class="flex items-center gap-1">
          <PhCertificate @size={{16}} class="text-violet-500" />
          <span>{{@coach.yearsExperience}}+ years</span>
        </div>
        <div class="flex items-center gap-1">
          <PhCalendarCheck @size={{16}} class="text-emerald-500" />
          <span>{{@coach.availableSlots}} slots this week</span>
        </div>
      </div>

      {{! Specialties }}
      <div class="flex flex-wrap gap-2">
        {{#each @coach.specialties as |specialty|}}
          <Chip
            class="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
          >
            {{specialty}}
          </Chip>
        {{/each}}
      </div>

      {{! Bio preview }}
      {{#unless @compact}}
        <Text class="text-gray-600 text-sm line-clamp-2">
          {{@coach.bio}}
        </Text>
      {{/unless}}

      {{! Credentials }}
      <div class="pt-2 border-t border-gray-100">
        <Text class="text-xs text-gray-500 mb-2">
          Credentials
        </Text>
        <div class="flex flex-wrap gap-1">
          {{#each @coach.credentials as |credential|}}
            <span class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
              {{credential}}
            </span>
          {{/each}}
        </div>
      </div>

      {{! Actions }}
      <div class="flex gap-3 pt-2">
        {{#if @onSchedule}}
          <Button
            @intent="primary"
            @appearance="outlined"
            class="flex-1"
            {{on "click" (fn @onSchedule @coach)}}
          >
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{else}}
          <Button @intent="primary" @appearance="outlined" class="flex-1">
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{/if}}
        <Button @intent="primary" @appearance="minimal" class="flex-1">
          <PhChatCircle @size={{16}} class="mr-2" />
          Message
        </Button>
      </div>
    </div>
  </div>
</template>;

export default CoachCard;
